import { api } from "./api";

export type TransactionDirection = "INCOME" | "EXPENSE" | "TRANSFER";

export interface Category {
  id: string;
  name: string;
  direction: TransactionDirection;
  parentId: string | null;
  color: string | null;
  fullPath: string;
  createdAt: string;
  updatedAt: string;
  transactionCount: number;
}

export interface Transaction {
  id: string;
  accountId: string;
  account: { id: string; name: string };
  categoryId: string | null;
  category: { id: string; name: string; color: string | null } | null;
  date: string;
  amountCents: number;
  direction: TransactionDirection;
  description: string;
  notes: string | null;
  externalRef: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionsList {
  transactions: Transaction[];
  total: number;
  hasMore: boolean;
}

export interface CategorySummary {
  categoryId: string | null;
  categoryName: string;
  categoryColor: string | null;
  direction: TransactionDirection | null;
  totalCents: number;
  count: number;
}

export interface CreateCategoryInput {
  name: string;
  direction: TransactionDirection;
  parentId?: string | null;
  color?: string | null;
}

export type UpdateCategoryInput = Partial<{
  name: string;
  direction: TransactionDirection;
  parentId: string | null;
  color: string | null;
}>;

export interface CreateTransactionInput {
  accountId: string;
  categoryId?: string | null;
  date: string;
  amountCents: number;
  direction: TransactionDirection;
  description: string;
  notes?: string;
  externalRef?: string;
}

export type UpdateTransactionInput = Partial<{
  categoryId: string | null;
  date: string;
  amountCents: number;
  direction: TransactionDirection;
  description: string;
  notes: string | null;
}>;

export interface ListTransactionsFilters {
  accountId?: string;
  categoryId?: string;
  uncategorized?: boolean;
  direction?: TransactionDirection;
  from?: string;
  to?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

function qs(filters: ListTransactionsFilters): string {
  const sp = new URLSearchParams();
  if (filters.accountId) sp.set("accountId", filters.accountId);
  if (filters.categoryId) sp.set("categoryId", filters.categoryId);
  if (filters.uncategorized !== undefined) sp.set("uncategorized", String(filters.uncategorized));
  if (filters.direction) sp.set("direction", filters.direction);
  if (filters.from) sp.set("from", filters.from);
  if (filters.to) sp.set("to", filters.to);
  if (filters.search) sp.set("search", filters.search);
  if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
  if (filters.offset !== undefined) sp.set("offset", String(filters.offset));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const transactionsApi = {
  // Categories
  listCategories: (direction?: TransactionDirection) =>
    api<{ categories: Category[] }>(`/categories${direction ? `?direction=${direction}` : ""}`),
  createCategory: (input: CreateCategoryInput) =>
    api<Category>("/categories", { method: "POST", body: input }),
  updateCategory: (id: string, input: UpdateCategoryInput) =>
    api<Category>(`/categories/${id}`, { method: "PATCH", body: input }),
  deleteCategory: (id: string) => api<void>(`/categories/${id}`, { method: "DELETE" }),
  // Transactions
  listTransactions: (filters: ListTransactionsFilters = {}) =>
    api<TransactionsList>(`/transactions${qs(filters)}`),
  getTransaction: (id: string) => api<Transaction>(`/transactions/${id}`),
  createTransaction: (input: CreateTransactionInput) =>
    api<Transaction>("/transactions", { method: "POST", body: input }),
  updateTransaction: (id: string, input: UpdateTransactionInput) =>
    api<Transaction>(`/transactions/${id}`, { method: "PATCH", body: input }),
  deleteTransaction: (id: string) => api<void>(`/transactions/${id}`, { method: "DELETE" }),
  bulkCategorize: (transactionIds: string[], categoryId: string | null) =>
    api<{ updated: number }>("/transactions/bulk-categorize", {
      method: "POST",
      body: { transactionIds, categoryId },
    }),
  summaryByCategory: (filters: { from?: string; to?: string; accountId?: string } = {}) =>
    api<{ summary: CategorySummary[] }>(
      `/transactions/summary/by-category${qs(filters as ListTransactionsFilters)}`,
    ),
};
